import { useState, useRef, useCallback, useEffect } from 'react';

const livekit = require('livekit-client');

// Apply a sink to one audio element. For the OS default ('' or 'default') reset to native output
// ('') rather than forcing setSinkId('default'), which silences playback on some devices.
const applySink = (el, deviceId) => {
    if (typeof el.setSinkId !== 'function') return Promise.resolve();
    const sink = (!deviceId || deviceId === 'default') ? '' : deviceId;
    return el.setSinkId(sink).catch(error => console.error('Error setting audio sink:', error));
};

// useMicDevices — manages microphone + speaker device enumeration/selection, a live input level
// meter, and audio output routing for the agent test panel. Extracted from SingleAgent/index.js
// to keep that file under the 1000-line limit.
const useMicDevices = () => {
    const [micState, setMicState] = useState({ open: false, devices: [], selectedId: '', level: 0, speakers: [], speakerId: '' });
    const updateMic = useCallback((patch) => setMicState(prev => ({ ...prev, ...patch })), []);

    const meterStreamRef = useRef(null);   // MediaStream feeding the meter
    const meterRafRef = useRef(null);      // requestAnimationFrame id
    const audioCtxRef = useRef(null);      // AudioContext for the analyser
    const selectedIdRef = useRef('');      // mirror of mic selectedId for use in the call lifecycle
    const speakerIdRef = useRef('');       // mirror of selected speaker for use when audio elements attach
    const speakerUserSetRef = useRef(false); // true once the user actively picks a speaker from the dropdown
    const audioElementsRef = useRef([]);   // attached agent audio elements, for setSinkId routing

    // Enumerate available microphones; labels only populate after permission is granted
    const loadMicDevices = useCallback(async () => {
        try {
            const devices = await livekit.Room.getLocalDevices('audioinput');
            // Prefer the OS default, but skip the macOS Continuity "iPhone Microphone" (silent unless
            // you talk into the phone) — fall back to the first real device instead.
            const isPhone = d => /iphone|continuity/i.test(d['label'] || '');
            const defaultDevice = devices.find(d => d['deviceId'] === 'default' && !isPhone(d))
                || devices.find(d => !isPhone(d))
                || devices[0];
            setMicState(prev => {
                const selectedId = prev['selectedId'] || defaultDevice?.deviceId || '';
                selectedIdRef.current = selectedId;
                return { ...prev, devices, selectedId };
            });
        } catch (error) { console.error('Error enumerating microphones:', error); }
    }, []);

    // Resolve the mic device to publish for a call. Turns the OS 'default' alias into a concrete
    // device id and avoids macOS Continuity "iPhone Microphone" (which is silent unless you speak
    // into the phone). Returns a deviceId, or '' to let the browser pick.
    const resolveMicDeviceId = useCallback(async () => {
        try {
            const devices = await livekit.Room.getLocalDevices('audioinput');
            const isPhone = d => /iphone|continuity/i.test(d['label'] || '');
            const real = devices.filter(d => d['deviceId'] && d['deviceId'] !== 'default');
            const selected = devices.find(d => d['deviceId'] === selectedIdRef.current);

            // User picked a concrete, non-iPhone device — use it directly
            if (selected && selected['deviceId'] !== 'default' && !isPhone(selected)) return selected['deviceId'];

            // Selection is the OS 'default' alias (e.g. "Default - AirPods"). Resolve it to its REAL
            // twin — first by groupId (most reliable), then by stripped label name.
            if (selected && selected['deviceId'] === 'default') {
                // groupId match: the 'default' entry and its real counterpart share the same groupId
                if (selected['groupId']) {
                    const byGroup = real.find(d => d['groupId'] === selected['groupId'] && !isPhone(d));
                    if (byGroup) return byGroup['deviceId'];
                }
                // label match: strip "Default - " prefix, compare lowercased
                const name = (selected['label'] || '').replace(/^default\s*-\s*/i, '').trim().toLowerCase();
                const twin = real.find(d => (d['label'] || '').trim().toLowerCase() === name);
                if (twin && !isPhone(twin)) return twin['deviceId'];
            }

            // Fallback: first real, non-iPhone device, else any non-iPhone, else anything
            return (real.find(d => !isPhone(d)) || devices.find(d => !isPhone(d)) || devices[0])?.deviceId || '';
        } catch (error) { console.error('Error resolving mic device:', error); return ''; }
    }, []);

    // Enumerate available speakers (audio outputs); prefer the OS default
    const loadSpeakerDevices = useCallback(async () => {
        try {
            const speakers = await livekit.Room.getLocalDevices('audiooutput');
            const defaultSpeaker = speakers.find(d => d['deviceId'] === 'default') || speakers[0];
            setMicState(prev => {
                const speakerId = prev['speakerId'] || defaultSpeaker?.deviceId || '';
                speakerIdRef.current = speakerId;
                return { ...prev, speakers, speakerId };
            });
        } catch (error) { console.error('Error enumerating speakers:', error); }
    }, []);

    // Register an attached agent audio element. Don't touch the sink on attach — leave it on the
    // browser's native default output (the behavior that always worked). Only re-route if the user
    // has actively picked a speaker from the dropdown during this session.
    const registerAudioElement = useCallback((el) => {
        if (!el) return;
        audioElementsRef.current.push(el);
        if (speakerUserSetRef.current) applySink(el, speakerIdRef.current);
    }, []);

    // Switch the selected speaker; route all attached agent audio elements to it
    const handleSpeakerChange = useCallback(async (deviceId) => {
        speakerUserSetRef.current = true;
        updateMic({ speakerId: deviceId });
        speakerIdRef.current = deviceId;
        await Promise.all(audioElementsRef.current.map(el => applySink(el, deviceId)));
    }, [updateMic]);

    // Stop the live meter and release the mic stream / rAF / AudioContext
    const stopMeter = useCallback(() => {
        if (meterRafRef.current) cancelAnimationFrame(meterRafRef.current);
        meterRafRef.current = null;
        if (meterStreamRef.current) meterStreamRef.current.getTracks().forEach(t => t.stop());
        meterStreamRef.current = null;
        if (audioCtxRef.current) { audioCtxRef.current.close(); audioCtxRef.current = null; }
        updateMic({ level: 0 });
    }, [updateMic]);

    // Start a live input level meter for the given device
    const startMeter = useCallback(async (deviceId) => {
        stopMeter();
        try {
            const constraints = { audio: { deviceId: deviceId ? { exact: deviceId } : undefined, echoCancellation: true, noiseSuppression: true, autoGainControl: true } };
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            meterStreamRef.current = stream;

            // Re-enumerate now that permission is granted so device labels appear
            loadMicDevices();

            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            audioCtxRef.current = audioCtx;
            const source = audioCtx.createMediaStreamSource(stream);
            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            const data = new Uint8Array(analyser.frequencyBinCount);

            const tick = () => {
                analyser.getByteFrequencyData(data);
                const avg = data.reduce((sum, v) => sum + v, 0) / data.length;
                updateMic({ level: Math.min(100, Math.round((avg / 255) * 100 * 1.8)) });
                meterRafRef.current = requestAnimationFrame(tick);
            };
            tick();
        } catch (error) { console.error('Error starting mic meter:', error); }
    }, [stopMeter, loadMicDevices, updateMic]);

    // Switch the selected device; when a room is connected, switch the live published track too
    const handleMicChange = useCallback(async (deviceId, roomInstance) => {
        updateMic({ selectedId: deviceId });
        selectedIdRef.current = deviceId;
        startMeter(deviceId);
        if (roomInstance) {
            try { await roomInstance.switchActiveDevice('audioinput', deviceId); }
            catch (error) { console.error('Error switching active mic device:', error); }
        }
    }, [updateMic, startMeter]);

    // Forget attached audio elements (call this when the room disconnects)
    const clearAudioElements = useCallback(() => { audioElementsRef.current = []; }, []);

    // Toggle the inline settings panel; manage the meter lifecycle with it
    const toggleMicPanel = useCallback(() => {
        setMicState(prev => {
            const open = !prev['open'];
            if (open) { loadMicDevices(); loadSpeakerDevices(); startMeter(prev['selectedId']); }
            else stopMeter();
            return { ...prev, open };
        });
    }, [loadMicDevices, loadSpeakerDevices, startMeter, stopMeter]);

    // Pre-select the default input/output on mount; re-resolved with full labels once the panel opens
    useEffect(() => { loadMicDevices(); loadSpeakerDevices(); }, [loadMicDevices, loadSpeakerDevices]);

    // Release the mic on unmount
    useEffect(() => () => stopMeter(), [stopMeter]);

    return { micState, selectedIdRef, speakerIdRef, resolveMicDeviceId, loadMicDevices, startMeter, stopMeter, handleMicChange, handleSpeakerChange, registerAudioElement, clearAudioElements, toggleMicPanel };
};

export default useMicDevices;
